import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Video, X, Clock, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useWebSocket } from "@/context/WebSocketContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_INTERVAL_MS = 15_000;
const DISMISSED_STORAGE_KEY = "tmcteletech_dismissed_adhoc_invites";

/**
 * AdhocInviteBanner
 * -----------------
 * Persistent, prominent card shown at the top of the dashboard whenever
 * the current user has one or more active ad-hoc meeting invitations.
 *
 * Why this exists: before v1.9.12 the ad-hoc invite was ONLY a
 * Sonner toast with a 30-second `duration`. Facilitators who weren't
 * looking at their screen (or who reloaded the tab, or missed the WS
 * message) silently lost the invite and could not join. The backend
 * already had `/api/video/adhoc-meeting/active-invites` for exactly
 * this fallback but nothing was polling it. This component fixes both.
 *
 * Behavior:
 *   • Fetches active invites on mount, then polls every 15s.
 *   • Also listens to WS `adhoc_meeting_invite` events so incoming
 *     invites appear instantly without waiting for the next poll.
 *   • Removes an invite from the visible list once it expires, is
 *     dismissed by the user, or is joined.
 *   • Dismissals are stored in sessionStorage keyed by meeting_id so a
 *     re-poll doesn't nag the user — but a fresh session (new tab)
 *     will re-surface anything still active on the backend.
 */
function readDismissed() {
  try {
    const raw = sessionStorage.getItem(DISMISSED_STORAGE_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch {
    return new Set();
  }
}

function writeDismissed(setLike) {
  try {
    sessionStorage.setItem(
      DISMISSED_STORAGE_KEY,
      JSON.stringify(Array.from(setLike))
    );
  } catch {
    /* sessionStorage may be unavailable in private mode */
  }
}

function useCountdown(expiresAt) {
  const [remaining, setRemaining] = useState(() => msUntil(expiresAt));
  useEffect(() => {
    setRemaining(msUntil(expiresAt));
    const t = setInterval(() => setRemaining(msUntil(expiresAt)), 1000);
    return () => clearInterval(t);
  }, [expiresAt]);
  return remaining;
}

function msUntil(iso) {
  if (!iso) return 0;
  const d = new Date(iso).getTime();
  return Math.max(0, d - Date.now());
}

function formatRemaining(ms) {
  if (ms <= 0) return "expired";
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}m ${s.toString().padStart(2, "0")}s`;
}

function InviteCard({ invite, onJoin, onDismiss }) {
  const remaining = useCountdown(invite.expires_at);
  const disabled = remaining <= 0;
  return (
    <div
      className="rounded-lg border border-emerald-300 bg-emerald-50 shadow-sm p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
      data-testid={`adhoc-invite-card-${invite.id}`}
    >
      <div className="flex items-start gap-3 min-w-0 flex-1">
        <div className="w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center text-white shrink-0">
          <Video className="w-5 h-5" />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-emerald-900">
              Incoming video call
            </span>
            <span className="inline-flex items-center gap-1 text-xs text-emerald-800/70">
              <Clock className="w-3 h-3" />
              <span data-testid={`adhoc-invite-countdown-${invite.id}`}>
                {formatRemaining(remaining)}
              </span>
            </span>
          </div>
          <div className="text-sm text-emerald-900/90 mt-0.5 flex items-center gap-1 min-w-0">
            <User className="w-3.5 h-3.5 shrink-0" />
            <span className="truncate">
              <strong>{invite.initiator_name || "A colleague"}</strong>
              {invite.initiator_role ? ` (${invite.initiator_role})` : ""} is
              inviting you
            </span>
          </div>
          {invite.topic && invite.topic !== "Ad-hoc meeting" && (
            <div className="text-xs text-emerald-900/70 mt-0.5 truncate">
              Topic: {invite.topic}
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onDismiss(invite)}
          className="text-emerald-900/70 hover:text-emerald-900 hover:bg-emerald-100"
          data-testid={`adhoc-invite-dismiss-${invite.id}`}
        >
          <X className="w-4 h-4 mr-1" />
          Dismiss
        </Button>
        <Button
          size="sm"
          onClick={() => onJoin(invite)}
          disabled={disabled}
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid={`adhoc-invite-join-${invite.id}`}
        >
          <Video className="w-4 h-4 mr-2" />
          Join Meeting
        </Button>
      </div>
    </div>
  );
}

export function AdhocInviteBanner() {
  const { token, user } = useAuth();
  const { lastMessage } = useWebSocket();
  const navigate = useNavigate();
  const [invites, setInvites] = useState([]);
  const [dismissed, setDismissed] = useState(() => readDismissed());
  const pollRef = useRef(null);

  const fetchInvites = useCallback(async () => {
    if (!token) return;
    try {
      const resp = await axios.get(`${API}/video/adhoc-meeting/active-invites`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInvites(resp.data.invites || []);
    } catch (err) {
      // 401 is handled globally by AuthContext interceptor.
      // Any other transient error — silent retry on next poll.
    }
  }, [token]);

  // Initial fetch + polling.
  useEffect(() => {
    if (!token || !user) return;
    fetchInvites();
    pollRef.current = setInterval(fetchInvites, POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [token, user, fetchInvites]);

  // Also react instantly to WebSocket adhoc_meeting_invite events so we
  // don't wait up to 15s for the poll on freshly-received invites.
  useEffect(() => {
    if (!lastMessage) return;
    if (lastMessage.type === "adhoc_meeting_invite") {
      // Cheapest correct thing: re-fetch. Ensures we get the fully
      // normalized DB row (initiator_role, topic, expires_at).
      fetchInvites();
    }
  }, [lastMessage, fetchInvites]);

  // Auto-drop expired invites without another API round-trip.
  useEffect(() => {
    const t = setInterval(() => {
      setInvites((prev) =>
        prev.filter((i) => msUntil(i.expires_at) > 0)
      );
    }, 10_000);
    return () => clearInterval(t);
  }, []);

  const handleJoin = (invite) => {
    // Optimistically clear locally — the meeting page owns the session
    // from here. If they leave without joining fully, the poll will
    // re-surface it.
    setInvites((prev) => prev.filter((i) => i.id !== invite.id));
    navigate(`/adhoc-meeting/${invite.id}`);
  };

  const handleDismiss = (invite) => {
    const next = new Set(dismissed);
    next.add(invite.id);
    setDismissed(next);
    writeDismissed(next);
  };

  const visible = invites.filter((i) => !dismissed.has(i.id));

  if (visible.length === 0) return null;

  return (
    <div
      className="space-y-2 mb-4"
      role="region"
      aria-label="Incoming video call invitations"
      data-testid="adhoc-invite-banner"
    >
      {visible.map((invite) => (
        <InviteCard
          key={invite.id}
          invite={invite}
          onJoin={handleJoin}
          onDismiss={handleDismiss}
        />
      ))}
    </div>
  );
}

export default AdhocInviteBanner;
